import { FormEvent } from 'react';
import { Checkbox, Col, ControlLabel, FormControl, FormGroup, HelpBlock, Row } from 'react-bootstrap'
import { IBaseDataValidation, TypeOption } from './data-model';
import { IListItem, INewDonorRecipientBaseData, IRecipient } from '@nkr/models/index';
import { MedicalRecordLinkEmail } from './medical-record-link-email';
import MaskedInput from 'react-maskedinput';
import SelectPatient from './select-patient';

interface IProps {
    centerId: number,
    data: INewDonorRecipientBaseData,
    validation: IBaseDataValidation,
    newPatient: boolean,
    selectedRecipientId: number,
    addPairedDonorOnly: boolean,
    recipientHasCheekSwabResults: boolean,
    recipientList: IRecipient[],
    genderList: IListItem[],
    bloodTypeList: IListItem[],
    stateList: IListItem[],
    onFieldChange: (name: string, value: any) => void,
    onRecipientSelect: (list: number[]) => void
}

const RecipientDetails = (props: IProps) => {
    let { alias, mrn, gender, dateOfBirth, email, phoneNumber, firstName, lastName, bloodType, address, city, state, postalCode, shipTypingSwabs, medicalRecordId } = props.data;

    const handleFieldChange = (e: FormEvent<FormControl & HTMLInputElement & HTMLSelectElement>) => {
        let { name, value } = e.currentTarget;

        if (['shipTypingSwabs'].indexOf(name) >= 0) {
            props.onFieldChange(name, parseInt(value));
        } else {
            props.onFieldChange(name, value);
        }
    };

    const showFields = (props.newPatient == false && props.selectedRecipientId > 0) || props.newPatient;

    return (
        <>
            {(props.newPatient == false && props.addPairedDonorOnly == false) && (
                <FormGroup>
                    <ControlLabel>Select an existing recipient...</ControlLabel>
                    <SelectPatient
                        recipientId={props.selectedRecipientId}
                        recipientList={props.recipientList}
                        onSelected={props.onRecipientSelect} />
                </FormGroup>
            )}

            {showFields && (
                <>
                    <MedicalRecordLinkEmail
                        centerId={props.centerId}
                        email={email}
                        medicalRecordId={medicalRecordId}
                        validation={props.validation}
                        disabled={props.addPairedDonorOnly}
                        showMedicalRecord={props.newPatient}
                        onFieldChange={props.onFieldChange}
                    />

                    <Row>
                        <Col xs={12} lg={6} lgOffset={0}>
                            <FormGroup validationState={props.validation.alias == false || props.validation.existingAlias ? 'error' : null}>
                                <ControlLabel>Alias</ControlLabel>
                                <FormControl
                                    name='alias'
                                    disabled={props.addPairedDonorOnly}
                                    value={alias}
                                    onChange={handleFieldChange}
                                    maxLength={12}
                                />

                                {props.validation.existingAlias && (
                                    <HelpBlock>Alias must be unique</HelpBlock>
                                )}
                            </FormGroup>
                        </Col>

                        <Col xs={12} lg={6} lgOffset={0}>
                            <FormGroup validationState={props.validation.mrn == false || props.validation.existingMrn ? 'error' : null}>
                                <ControlLabel>MRN</ControlLabel>
                                <FormControl
                                    name='mrn'
                                    disabled={props.addPairedDonorOnly}
                                    value={mrn}
                                    onChange={handleFieldChange}
                                    maxLength={24}
                                />

                                {props.validation.existingMrn && (
                                    <HelpBlock>The MRN is not unique. There is already a recipient record with this MRN. If the recipient is deleted, contact NKR Operations.</HelpBlock>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12} lg={6} lgOffset={0}>
                            <FormGroup validationState={props.validation.gender == false ? 'error' : null}>
                                <ControlLabel>Gender</ControlLabel>
                                <FormControl
                                    componentClass='select'
                                    name='gender'
                                    disabled={props.addPairedDonorOnly}
                                    value={gender}
                                    onChange={handleFieldChange}>
                                    <option value={''}>- Please Select -</option>
                                    {props.genderList.map(item =>
                                        <option key={'gender-' + item.id} value={item.abbreviation}>{item.description}</option>
                                    )}
                                </FormControl>
                            </FormGroup>
                        </Col>

                        <Col xs={12} lg={6} lgOffset={0}>
                            <FormGroup validationState={props.validation.dateOfBirth == false ? 'error' : null}>
                                <ControlLabel>Date of Birth</ControlLabel>
                                <MaskedInput
                                    name='dateOfBirth'
                                    className='form-control'
                                    mask='11/11/1111'
                                    disabled={props.addPairedDonorOnly}
                                    value={dateOfBirth}
                                    onChange={handleFieldChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12} lg={6} lgOffset={0}>
                            <FormGroup validationState={props.validation.firstName == false ? 'error' : null}>
                                <ControlLabel>First Name</ControlLabel>
                                <FormControl
                                    name='firstName'
                                    disabled={props.addPairedDonorOnly}
                                    value={firstName}
                                    onChange={handleFieldChange} />
                            </FormGroup>
                        </Col>

                        <Col xs={12} lg={6} lgOffset={0}>
                            <FormGroup validationState={props.validation.lastName == false ? 'error' : null}>
                                <ControlLabel>Last Name</ControlLabel>
                                <FormControl
                                    name='lastName'
                                    disabled={props.addPairedDonorOnly}
                                    value={lastName}
                                    onChange={handleFieldChange} />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12} lg={6} lgOffset={0}>
                            <FormGroup validationState={props.validation.bloodType == false ? 'error' : null}>
                                <ControlLabel>Blood Type</ControlLabel>
                                <FormControl
                                    componentClass='select'
                                    name='bloodType'
                                    disabled={props.addPairedDonorOnly}
                                    value={bloodType}
                                    onChange={handleFieldChange}>
                                    <option value={''}>- Please Select -</option>
                                    {props.bloodTypeList.map(item =>
                                        <option key={'abo-' + item.id} value={item.abbreviation}>{item.description}</option>
                                    )}
                                </FormControl>
                            </FormGroup>
                        </Col>
                        <Col xs={12} lg={6} lgOffset={0}>
                            <FormGroup validationState={props.validation.phoneNumber == false ? 'error' : null}>
                                <ControlLabel>Phone Number</ControlLabel>
                                <FormControl
                                    name='phoneNumber'
                                    disabled={props.addPairedDonorOnly}
                                    value={phoneNumber}
                                    onChange={handleFieldChange} />
                                {props.validation.phoneNumber === false && (
                                    <HelpBlock>Please enter a valid phone number.</HelpBlock>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12} lg={12} lgOffset={0}>
                            <FormGroup validationState={props.validation.address == false ? 'error' : null}>
                                <ControlLabel>Address</ControlLabel>
                                <FormControl
                                    name='address'
                                    disabled={props.addPairedDonorOnly}
                                    value={address}
                                    onChange={handleFieldChange} />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12} lg={5} lgOffset={0}>
                            <FormGroup validationState={props.validation.city == false ? 'error' : null}>
                                <ControlLabel>City</ControlLabel>
                                <FormControl
                                    name='city'
                                    disabled={props.addPairedDonorOnly}
                                    value={city}
                                    onChange={handleFieldChange} />
                            </FormGroup>
                        </Col>

                        <Col xs={12} lg={4} lgOffset={0}>
                            <FormGroup validationState={props.validation.state == false ? 'error' : null}>
                                <ControlLabel>State</ControlLabel>
                                <FormControl
                                    componentClass='select'
                                    name='state'
                                    disabled={props.addPairedDonorOnly}
                                    value={state}
                                    onChange={handleFieldChange}>
                                    <option value={''}>- Please Select -</option>
                                    {props.stateList.map(item =>
                                        <option key={'state-' + item.id} value={item.abbreviation}>{item.description}</option>
                                    )}
                                </FormControl>
                            </FormGroup>
                        </Col>

                        <Col xs={12} lg={3} lgOffset={0}>
                            <FormGroup validationState={props.validation.postalCode == false ? 'error' : null}>
                                <ControlLabel>Postal Code</ControlLabel>
                                <FormControl
                                    name='postalCode'
                                    disabled={props.addPairedDonorOnly}
                                    value={postalCode}
                                    onChange={handleFieldChange} />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12} lg={12} lgOffset={0}>
                            <FormGroup validationState={props.validation.shipTypingSwabs == false ? 'error' : null}>
                                <ControlLabel>High Resolution Tissue Typing (Cheek Swab) <span style={{ color: 'red' }}>*</span></ControlLabel>
                                <FormControl
                                    componentClass='select'
                                    name='shipTypingSwabs'
                                    disabled={props.addPairedDonorOnly || props.recipientHasCheekSwabResults}
                                    style={{ width: 'initial' }}
                                    value={shipTypingSwabs}
                                    onChange={handleFieldChange}
                                >
                                    <option value={-1}>- Please Select -</option>
                                    <option value={1}>Order High Res Tissue Typing (Cheek Swab)</option>
                                    <option value={2}>Utilize a Bulk Cheek Swab (Kit On-hand)</option>
                                </FormControl>
                            </FormGroup>
                        </Col>
                    </Row>
                </>
            )}
        </>
    );
}

export default RecipientDetails;